import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import NewNavbar from "../components/NewNavbar";
import Footer from "../components/Footer";
import { useOrdersListQuery } from "../store/api.ts";
import SEO from "../components/SEO.js";
import {
  formatPrice,
  getFirstAvailableAmount,
  getOrderItemQuantity,
  getOrderItemLineTotal,
  getOrderItemOriginalLineTotal,
  getOrderItemSavings,
  hasOrderItemDiscount,
  getOrderTotalAmount,
  getOrderSavingsAmount,
  getOrderTotalItems,
  toAmount,
} from "../utils.js";
import { getOrderBundlePricing, groupOrderItems } from "../utils/orderBundle";

const getItemQuantity = getOrderItemQuantity;

const getItemOriginalUnitPrice = (item) =>
  getFirstAvailableAmount(
    item?.original_price,
    item?.price,
    item?.unit_price,
  );

const getItemUnitPrice = (item) =>
  getFirstAvailableAmount(
    item?.unit_price,
    item?.price,
    item?.original_price,
  );

const getItemOriginalTotal = (item) => getOrderItemOriginalLineTotal(item, getItemOriginalUnitPrice);

const getItemLineTotal = (item) => getOrderItemLineTotal(item, getItemUnitPrice);

const getItemSavings = (item) =>
  getOrderItemSavings({
    item,
    getOriginalUnitPrice: getItemOriginalUnitPrice,
    getLineTotal: getItemLineTotal,
  });

const hasItemDiscount = (item) => hasOrderItemDiscount(item, getItemSavings);

const getOrderTotal = (order) => getOrderTotalAmount(order, getItemLineTotal);

const getOrderSavings = (order) => getOrderSavingsAmount(order, getItemSavings);

const getTotalItems = getOrderTotalItems;

const OrderTotals = ({ order }) => {
  const total = getOrderTotal(order);
  const savings = getOrderSavings(order);
  const subtotal = total + savings;

  return (
    <div className="mt-6 flex justify-end border-t border-[#E5E5E5] pt-5">
      <div className="w-full max-w-[280px] space-y-3">
        <div className="flex items-center justify-between gap-4 text-sm">
          <span className="text-gray-500">Subtotal</span>
          <span className="font-medium text-black">
            ${formatPrice(subtotal)}
          </span>
        </div>

        {savings > 0 ? (
          <div className="flex items-center justify-between gap-4 text-sm">
            <span className="text-[#257A45]">Discount</span>
            <span className="font-medium text-[#257A45]">
              -${formatPrice(savings)}
            </span>
          </div>
        ) : null}

        <div className="flex items-center justify-between gap-4 border-t border-[#E5E5E5] pt-3">
          <span className="font-medium text-black">Total</span>
          <span className="text-lg font-semibold text-black">
            ${formatPrice(total)}
          </span>
        </div>
      </div>
    </div>
  );
};

const OrderBundleDetails = ({ bundle }) => {
  const pricing = getOrderBundlePricing(bundle);

  return (
    <div className="overflow-hidden rounded-[6px] border border-[#D8D3CC] bg-white">
      {/* HEADER */}
      <div className="border-b border-[#DDD8D1] bg-[#F7F4EF] px-4 py-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <span className="inline-flex rounded-[3px] bg-black px-2 py-1 font-spaceMono text-[8px] sm:text-[11px] font-bold uppercase tracking-[0.16em] text-white">
              Bundle
            </span>
            <h3 className="mt-2 text-[15px] font-semibold text-black">
              {bundle?.name || "Collection Bundle"}
            </h3>
            <p className="mt-1 font-spaceMono text-[9px] sm:text-[12px] text-[#777777]">
              {bundle?.items?.length || 0}{" "}
              products
              {pricing.quantity > 1
                ? ` • Bundle Qty ${pricing.quantity}`
                : ""}
            </p>
          </div>
          {pricing.discountPercent > 0 ? (
            <span className="rounded-[3px] bg-[#EAF7EE] px-2 py-1 text-[10px] sm:text-[12px] font-semibold text-[#257A45]">
              {pricing.discountPercent} % OFF
            </span>
          ) : null}
        </div>
      </div>

      {/* PRODUCTS */}
      <div className="divide-y divide-[#E5E5E5]">
        {(bundle?.items || []).map(
          (item, index) => {
            const quantity = getItemQuantity(item);
            const originalUnitPrice = getItemOriginalUnitPrice(item);
            const image = item?.product_image;

            return (
              <div
                key={`${bundle.id}-${index}`}
                className="flex flex-col gap-4 p-4 sm:flex-row"
              >
                <div className="h-20 w-20 shrink-0 overflow-hidden rounded-md bg-[#D3D3D3]">
                  {image ? (
                    <img
                      src={image}
                      alt={item?.product_name || "Product"}
                      className="h-full w-full object-cover"
                    />
                  ) : null}
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="font-medium">
                    {item?.product_name}
                  </h4>
                  <p className="mt-1 text-sm text-gray-500">
                    Size:{" "}{item?.size || "—"}
                  </p>
                  <div className="mt-1 flex items-center gap-2 text-sm text-gray-500">
                    <span>Color: </span>
                    {item?.color ? (
                      <>
                        <span
                          className="inline-block h-3.5 w-3.5 rounded-full border border-[#D9D9D9]"
                          style={{
                            backgroundColor: item.color,
                          }}
                        />
                        <span>{item.color}</span>
                      </>
                    ) : (
                      <span>—</span>
                    )}
                  </div>
                  <p className="mt-3 text-sm">
                    ${formatPrice(originalUnitPrice)}{" "}
                    original price
                  </p>
                </div>
                <div className="shrink-0 text-left sm:text-right">
                  <p className="font-semibold text-black">
                    ${formatPrice(originalUnitPrice * quantity)}
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    Qty: {quantity}
                  </p>
                </div>
              </div>
            );
          },
        )}
      </div>

      {/* BUNDLE PRICE */}
      <div className="border-t border-[#DDD8D1] bg-[#FAF9F7] px-4 py-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">
            Original Bundle Price
          </span>
          <span className="font-medium">
            ${formatPrice(pricing.originalSubtotal)}
          </span>
        </div>

        {pricing.discountAmount > 0 ? (
          <div className="mt-3 flex items-center justify-between text-sm text-[#257A45]">
            <span>
              Bundle Discount ({pricing.discountPercent}%)
            </span>
            <span className="font-medium">
              -${formatPrice(pricing.discountAmount)}
            </span>
          </div>
        ) : null}

        <div className="mt-3 flex items-center justify-between border-t border-[#DDD8D1] pt-3">
          <span className="font-semibold">
            Bundle Total
          </span>
          <span className="text-lg font-semibold">
            ${formatPrice(pricing.total)}
          </span>
        </div>
      </div>
    </div>
  );
};

const Orders = () => {
  const isAuthenticated = typeof window !== "undefined" && Boolean(localStorage.getItem("authToken"));
  const navigate = useNavigate();

  const { data: orders = [] } = useOrdersListQuery( undefined, { skip: !isAuthenticated});    
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const getOrderItemSlug = (item) => String(item?.product_slug ?? item?.slug ?? "").trim();
  const handleViewProduct = (item) => {
    const slug = getOrderItemSlug(item);
    if (!slug) {
      console.warn("Order item does not contain a product slug:", item);
      return;
    }
    navigate(`/product/${slug}`);
  };

  const handleDetailsClick = (order) => {
    setSelectedOrder(order);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedOrder(null);
  };

  const getOrderStatus = (order) => {
    return order?.status || "Processing";
  };

  return (
    <div className="min-h-screen bg-[#F6F6F6]">
      <SEO
        title="My Orders | View Your Eveness Purchases"
        description="View your Eveness order history, order details, product purchases, delivery information, and account order activity."
        path="/account/orders"
      />
      <NewNavbar bottomNav="cart" />

      <main className="mx-auto w-full max-w-[1080px] px-3 pb-16 pt-6 sm:px-6 md:px-4 md:pt-8 lg:px-8">
        <section>
          <h1 className="text-[26px] font-bold uppercase leading-[32px] text-black md:text-[32px] md:leading-[38px]">
            My Orders
          </h1>

          <p className="mt-2 text-[12px] uppercase tracking-[0.12em] text-[#8B93A3]">
            &gt; Structural parameters for algorithm alignment.
          </p>

          <div className="mt-4 h-px w-[340px] max-w-full bg-[#CFCFCF]" />
        </section>

        <section className="mt-5 space-y-4">
          {orders.length === 0 && (
            <div className="rounded-[6px] border border-[#D9D9D9] bg-white px-6 py-12 text-center text-[14px] text-[#7B7B87]">
              No orders found.
            </div>
          )}

          {orders.map((order) => {
            const firstItem = order.items?.[0];
            const totalPrice = getOrderTotal(order);
            const totalSavings = getOrderSavings(order);
            const originalTotal = totalPrice + totalSavings;
            const totalItems = getTotalItems(order.items);
            const hasDiscount = totalSavings > 0;

            return (
              <article
                key={order.id}
                className="rounded-[6px] border border-[#D9D9D9] bg-white px-3 py-3 md:px-5 md:py-4"
              >
                <div className="grid gap-3 md:grid-cols-[1fr_180px_170px] md:items-start md:gap-4">
                  <div className="flex min-w-0 gap-3 md:gap-4">
                    <div className="h-[64px] w-[64px] shrink-0 overflow-hidden rounded-[4px] bg-[#D3D3D3] md:h-[66px] md:w-[68px]">
                      {firstItem?.product_image ? (
                        <img
                          src={firstItem.product_image}
                          alt={firstItem.product_name || "Product"}
                          className="h-full w-full object-cover"
                        />
                      ) : null}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-start gap-2">
                        <h3 className="line-clamp-2 text-[14px] font-medium leading-[18px] text-black md:truncate">
                          {firstItem?.product_name || "No items in this order"}
                        </h3>
                        {firstItem?.bundle_group_id &&
                          firstItem?.bundle_name ? (
                            <p className="mt-1 font-spaceMono text-[9px] uppercase tracking-[0.08em] text-[#777777]">
                              Bundle:{" "}
                              {firstItem.bundle_name}
                            </p>
                          ) : null}

                        {toAmount(firstItem?.discount_percent) > 0 ? (
                          <span className="shrink-0 rounded-[3px] bg-[#EAF7EE] px-1.5 py-0.5 font-spaceMono text-[10px] font-medium text-[#257A45]">
                            {toAmount(firstItem.discount_percent)}% OFF
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-2 md:gap-3">
                        <span className="rounded-[4px] bg-[#E7F0FF] px-2 py-1 font-mono text-[12px] text-[#416C98]">
                          {order.items?.length > 1
                            ? `${totalItems} items`
                            : `Size: ${firstItem?.size || "—"}`}
                        </span>

                        <button
                          type="button"
                          onClick={() => handleViewProduct(firstItem)}
                          className="text-[12px] text-[#4B4F63] hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          View Product
                        </button>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleDetailsClick(order)}
                        className="mt-3 h-[28px] w-full rounded-[4px] bg-[#313131] font-spaceMono text-[12px] text-white transition hover:opacity-90 md:h-[24px] md:max-w-[286px]"
                      >
                        View Details
                      </button>
                    </div>
                  </div>

                  <div className="flex h-full flex-col justify-start border-t border-[#ECECEC] pt-3 md:items-start md:border-t-0 md:pt-0">
                    {hasDiscount ? (
                      <p className="text-[12px] text-[#8A8A8A] line-through">
                        ${formatPrice(originalTotal)}
                      </p>
                    ) : null}

                    <p className="text-[14px] font-semibold text-black">
                      ${formatPrice(totalPrice)}
                    </p>

                    {hasDiscount ? (
                      <p className="mt-1 text-[11px] font-medium text-[#257A45]">
                        Saved ${formatPrice(totalSavings)}
                      </p>
                    ) : null}
                  </div>

                  <div className="flex h-full flex-row items-center justify-between gap-3 border-t border-[#ECECEC] pt-3 md:flex-col md:items-start md:justify-start md:gap-4 md:border-t-0 md:pt-0">
                    <div className="text-[14px] font-semibold text-black">
                      {getOrderStatus(order)}
                    </div>

                    <div className="text-[13px] text-[#7B7B87]">
                      Order #{order.id}
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </section>
      </main>

      <Modal isOpen={isModalOpen} onClose={closeModal}>
        {selectedOrder && (
          <div className="w-full max-w-2xl p-6">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-[26px] font-bold uppercase">Order Details</h2>

              <button
                type="button"
                onClick={closeModal}
                className="text-[22px] leading-none text-[#313131]"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              {groupOrderItems(selectedOrder.items || []).map((entry) => {
                if (entry.type === "bundle") {
                  return (
                    <OrderBundleDetails
                      key={entry.key}
                      bundle={entry}
                    />
                  );
                }
                const item = entry.item;
                const quantity = getItemQuantity(item);
                const originalUnitPrice = getItemOriginalUnitPrice(item);
                const unitPrice = getItemUnitPrice(item);
                const originalLineTotal = getItemOriginalTotal(item);
                const lineTotal = getItemLineTotal(item);
                const savings = getItemSavings(item);
                const hasDiscount = hasItemDiscount(item);

                return (
                  <div
                    key={entry.key}
                    className="flex flex-col gap-4 border-b border-[#E5E5E5] pb-4 last:border-b-0 sm:flex-row"
                  >
                    <div className="h-20 w-20 shrink-0 overflow-hidden rounded-md bg-[#D3D3D3]">
                      {item.product_image ? (
                        <img
                          src={item.product_image}
                          alt={item.product_name || "Product"}
                          className="h-full w-full object-cover"
                        />
                      ) : null}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="font-medium">{item.product_name}</h4>

                        {toAmount(item.discount_percent) > 0 ? (
                          <span className="rounded-[3px] bg-[#EAF7EE] px-1.5 py-0.5 text-[10px] font-semibold text-[#257A45]">
                            {toAmount(item.discount_percent)}% OFF
                          </span>
                        ) : null}
                      </div>

                      <p className="mt-1 text-sm text-gray-500">
                        Size: {item.size || "—"}
                      </p>

                      <div className="mt-1 flex items-center gap-2 text-sm text-gray-500">
                        <span>Color:</span>

                        {item.color ? (
                          <>
                            <span
                              className="inline-block h-3.5 w-3.5 rounded-full border border-[#D9D9D9]"
                              style={{ backgroundColor: item.color }}
                            />
                            <span>{item.color}</span>
                          </>
                        ) : (
                          <span>—</span>
                        )}
                      </div>

                      <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                        {hasDiscount ? (
                          <span className="text-[#8A8A8A] line-through">
                            ${formatPrice(originalUnitPrice)}
                          </span>
                        ) : null}

                        <span className="font-semibold text-black">
                          ${formatPrice(unitPrice)} each
                        </span>
                      </div>
                    </div>

                    <div className="shrink-0 text-left sm:text-right">
                      {hasDiscount ? (
                        <p className="text-sm text-[#8A8A8A] line-through">
                          ${formatPrice(originalLineTotal)}
                        </p>
                      ) : null}

                      <p className="font-semibold text-black">
                        ${formatPrice(lineTotal)}
                      </p>

                      <p className="mt-1 text-sm text-gray-500">
                        Qty: {quantity}
                      </p>

                      {savings > 0 ? (
                        <p className="mt-1 text-xs font-medium text-[#257A45]">
                          You saved ${formatPrice(savings)}
                        </p>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>

            <OrderTotals order={selectedOrder} />
          </div>
        )}
      </Modal>
      <Footer />
    </div>
  );
};

export default Orders;

const Modal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center overflow-auto bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-full w-full max-w-3xl overflow-auto rounded-[8px] bg-white shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
};